<template>
  <div :class="wrapperClass">
    <table :class="tableClass">
      <thead>
        <slot name="head" />
      </thead>

      <tbody>
        <tr v-if="!items || items.length === 0" class="empty-row">
          <td :colspan="emptyColspan">
            <slot name="empty">{{ emptyText }}</slot>
          </td>
        </tr>

        <tr
          v-for="(item, index) in items"
          :key="getItemKey(item, index)"
          :class="resolveRowClass(item, index)"
          @click="$emit('row-click', item, index)"
        >
          <slot name="row" :item="item" :index="index" />
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
const props = defineProps({
  items: { type: Array, default: () => [] },

  keyField: { type: String, default: 'id' },
  rowKey: { type: Function, default: null },

  rowClass: { type: [String, Array, Object, Function], default: '' },

  emptyText: {
    type: String,
    default: 'موردی برای نمایش وجود ندارد.',
  },
  emptyColspan: { type: Number, default: 1 },

  // Defaults are intentionally aligned with the clinic-patients table so that
  // using <BaseTable> anywhere yields the same standard table appearance.
  wrapperClass: { type: [String, Array, Object], default: 'table-wrapper' },
  tableClass: { type: [String, Array, Object], default: 'maintable' },
});

defineEmits(['row-click']);

function getItemKey(item, index) {
  if (typeof props.rowKey === 'function') return props.rowKey(item, index);
  if (item && props.keyField && item[props.keyField] != null) return item[props.keyField];
  return index;
}

function resolveRowClass(item, index) {
  if (typeof props.rowClass === 'function') return props.rowClass(item, index);
  return props.rowClass;
}
</script>

<style>
/* Standard table wrapper (scroll + borders) */
.table-wrapper {
  overflow: auto;
  /* Enables both horizontal and vertical scrolling */
  max-height: 70vh;
  /* Set a max height for vertical scrolling */
  border: 1px solid var(--text-color-thirdly) !important;
  border-radius: 10px;

  /* Scrollbar that respects theme variables */
  scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
  scrollbar-width: thin;
}

.table-wrapper::-webkit-scrollbar {
  height: 10px;
  width: 10px;
  background: var(--scrollbar-track);
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb);
  border-radius: 8px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--text-color-secondary-rgb), 0.18);
}

/* Standard table styling (originally from clinic-patients) */
.maintable,
.patients-table {
  width: 100%;
  text-align: right;
  font-size: calc(0.3vw + 8px);
  color: var(--text-color-primary);
  border-collapse: separate;
  border-spacing: 0;
}

.maintable th,
.maintable td,
.patients-table th,
.patients-table td {
  padding: 12px 15px;
  border-bottom: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  white-space: nowrap;
}

.maintable td,
.patients-table td {
  border-left: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
}

.maintable td:last-child,
.patients-table td:last-child {
  border-left: none;
}

.maintable thead,
.patients-table thead {
  background-color: var(--hover-background-color);
  z-index: 2;
}

.maintable th,
.patients-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  font-weight: bold;
  border-left: 1px solid rgba(var(--text-color-secondary-rgb), 0.12);
  background-color: var(--hover-background-color);
}

.maintable th:last-child,
.patients-table th:last-child {
  border-left: none;
}

/* Sticky last column (RTL) */
.maintable th:last-child,
.maintable td:last-child,
.patients-table th:last-child,
.patients-table td:last-child {
  position: sticky;
  left: 0;
  z-index: 1;
  box-shadow: 10px 0px 10px -5px rgba(var(--text-color-secondary-rgb), 0.25);
}

.maintable th:last-child,
.patients-table th:last-child {
  background-color: var(--hover-background-color);
  z-index: 3;
}

.maintable td:last-child,
.patients-table td:last-child {
  background-color: var(--hover-background-color);
}

.maintable tbody tr:hover,
.patients-table tbody tr:hover {
  background-color: rgba(var(--text-color-secondary-rgb), 0.06);
}

.empty-row td {
  text-align: center;
  padding: 40px;
  color: var(--text-color-secondary);
}

/* Optional action cell/button styling used by clinic-patients */
.actions-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  margin: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: calc(0.2vw + 14px);
  height: 100%;
  min-height: 32px;
  width: 100%;
}

@media (max-width: 768px) {
  .table-wrapper {
    overflow: auto;
    min-height: 100vh !important;
    border: none !important;
    border-radius: 0px;
  }

  .maintable thead,
  .patients-table thead {
    display: none;
  }

  .maintable tr,
  .patients-table tr {
    display: block;
    border: 1px solid var(--tex-color-thirdly) !important;
    border-radius: 8px;
    margin-bottom: 1rem;
    padding: 0.5rem;
  }

  .maintable td,
  .patients-table td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0.5rem;
    border-bottom: 1px solid rgba(var(--text-color-secondary-rgb), 0.04);
    text-align: left;
    white-space: normal;
  }

  /* Hide extra fields on mobile (based on data-labels the consumer sets on <td>) */
  .maintable td[data-label="تلفن ثابت"],
  .maintable td[data-label="ردیف"],
  .maintable td[data-label="نوع بیمه"],
  .maintable td[data-label="شماره بیمه"],
  .maintable td[data-label="نام پدر"],
  .maintable td[data-label="نام همسر"],
  .maintable td[data-label="تاریخ مراجعه اولیه"],
  .maintable td[data-label="ش پرونده دستی"],
  .maintable td[data-label="معرف"],
  .maintable td[data-label="شغل"],
  .maintable td[data-label="جنسیت"],
  .maintable td[data-label="شماره ثابت"],
  .maintable td[data-label="تاریخ تولد"],
  .maintable td[data-label="توضیحات"],
  .patients-table td[data-label="تلفن ثابت"],
  .patients-table td[data-label="ردیف"],
  .patients-table td[data-label="نوع بیمه"],
  .patients-table td[data-label="شماره بیمه"],
  .patients-table td[data-label="نام پدر"],
  .patients-table td[data-label="نام همسر"],
  .patients-table td[data-label="تاریخ مراجعه اولیه"],
  .patients-table td[data-label="ش پرونده دستی"],
  .patients-table td[data-label="معرف"],
  .patients-table td[data-label="شغل"],
  .patients-table td[data-label="جنسیت"],
  .patients-table td[data-label="شماره ثابت"],
  .patients-table td[data-label="تاریخ تولد"],
  .patients-table td[data-label="توضیحات"] {
    display: none;
  }

  .maintable td:last-child,
  .patients-table td:last-child {
    border-bottom: none;
  }

  .maintable td::before,
  .patients-table td::before {
    content: attr(data-label);
    font-weight: bold;
    color: var(--tex-color-thirdly) !important;
    padding-left: 1rem;
    text-align: right;
  }

  .maintable td,
  .patients-table td {
    border-left: none !important;
  }

  .maintable th:last-child,
  .maintable td:last-child,
  .patients-table th:last-child,
  .patients-table td:last-child {
    box-shadow: 10px 0px 10px -5px rgba(var(--text-color-secondary-rgb), 0) !important;
  }

  .actions-cell {
    justify-content: center;
  }

  .action-btn {
    align-items: start !important;
  }
}
</style>
